import { BroadcastChannel, OnMessageHandler } from "broadcast-channel";
import { EventHandlerMap, BroadcastEvent, EventHandler } from "@/types/BroadcastChannelTypes";

/**
 * Utility class which can be used to pass messages, with associated payloads,
 * along a BroadcastChannel.
 */
class MessageBroadcastChannel {
	private broadcastChannel: BroadcastChannel<BroadcastEvent>;
	private eventHandlers: EventHandlerMap;

	/**
	 * Creates a new instance of the MessageBroadcastChannel class.
	 * @param channel The channel name to send/receive on.
	 */
	constructor(channel: string) {
		this.eventHandlers = {};
		this.broadcastChannel = new BroadcastChannel(channel);
		this.broadcastChannel.addEventListener("message", ev => this.onMessageReceived(ev));
	}

	/**
	 * Adds a new handler for an event.
	 * @param eventName The name of the event to be handled.
	 * @param handler The handler for the event payload.
	 */
	public addListener(eventName: string, handler: EventHandler) {
		if (!this.eventHandlers[eventName]) {
			this.eventHandlers[eventName] = [];
		}
		this.eventHandlers[eventName].push(handler);
	}

	/**
	 * Removes all handlers for an event.
	 * @param eventName The event handler to remove.
	 */
	public removeListener(eventName: string) {
		if (this.eventHandlers[eventName]) {
			delete this.eventHandlers[eventName];
		}
	}

	/**
	 * Removes a handler for an event.
	 * @param eventName The event handler to remove.
	 */
	public removeSpecificListener(eventName: string, handler: (eventData: any) => void) {
		if (!this.eventHandlers[eventName]) return;

		let handlerIndex = this.eventHandlers[eventName].findIndex(h => h === handler);
		this.eventHandlers[eventName].splice(handlerIndex, 1);
	}

	/**
	 * Posts an event to the channel.
	 * @param eventName The event name to post.
	 * @param eventData (Optional) Data to include with the post.
	 */
	public async postMessage(eventName: string, eventData?: any) {
		await this.broadcastChannel.postMessage({ eventName, eventData });
	}

	/**
	 * Returns a promise which resolves once a message matching the specified type is received.
	 * @param eventName The event to wait for.
	 * @returns The data (if any) sent with the message, otherwise null.
	 */
	public async waitForMessage(eventName: string): Promise<any> {
		return new Promise((resolve, reject) => {
			const messageHandler = (msg: BroadcastEvent) => {
				if (msg.eventName == eventName) {
					resolve(msg.eventData ?? null);

					// Remove the event listener after the promise is resolved
					this.broadcastChannel.removeEventListener("message", messageHandler);
				}
			};

			this.broadcastChannel.addEventListener("message", messageHandler);
		});
	}

	/**
	 * Closes the BroadcastChannel.
	 */
	public async close() {
		// Clear the event handler, remove handlers, and close the channel.
		this.broadcastChannel.removeEventListener("message", this.onMessageReceived);
		this.eventHandlers = {};

		await this.broadcastChannel.close();
	}

	/**
	 * Handler for when a message is received on the broadcast channel.
	 * @param event The event sent over the BroadcastChannel.
	 */
	private onMessageReceived(event: BroadcastEvent) {
		let handlers = this.eventHandlers?.[event.eventName];

		// If we have a handler for this message type, call it and pass it the
		// payload.
		handlers?.forEach(handler => {
			handler(event.eventData);
		});
	}

	/**
	 * Get the name of the broadcast channel.
	 */
	public get name(): string {
		return this.broadcastChannel.name;
	}
}

export { MessageBroadcastChannel };
